// The preview step's full body -- summary grid, degraded-source banner,
// kept-count/total line, match list, likely-related tray -- shared verbatim
// across phone/tablet/desktop containers (`#s07` phone; `#foundation` step 3;
// the same content is what the plan tells the desktop right panel to reuse).
import { AmountText } from '@/components/AmountText';
import type { AskProposal } from '@/api/hooks/useAskPreview';
import { AskProposalSummary } from './AskProposalSummary';
import { AskDegradedSourceBanner } from './AskDegradedSourceBanner';
import { AskMatchRow } from './AskMatchRow';
import { LikelyRelatedTray } from './LikelyRelatedTray';
import { decimalSum } from '@/features/transactions/decimal';

export function uniqueAskRows(proposal: AskProposal) {
  const seenIds = new Set<string>();
  return [...proposal.matches, ...proposal.likely_related].filter((row) => {
    if (seenIds.has(row.id)) return false;
    seenIds.add(row.id);
    return true;
  });
}

export function selectedAskRows(proposal: AskProposal, keptIds: Set<string>) {
  return uniqueAskRows(proposal).filter((row) => keptIds.has(row.id));
}

export function AskProposalBody({
  proposal,
  keptIds,
  onToggle,
}: {
  proposal: AskProposal;
  keptIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  const selectedRows = selectedAskRows(proposal, keptIds);
  const keptTotal = decimalSum(selectedRows.map((row) => row.amount));

  return (
    <div className="flex flex-col gap-2.5" data-testid="ask-proposal-body">
      {proposal.degraded_sources.map((source) => (
        <AskDegradedSourceBanner key={source.institution_name} institutionName={source.institution_name} reason={source.reason} />
      ))}
      <AskProposalSummary
        window={proposal.window}
        accountLast4s={proposal.account_last4s}
        eventName={proposal.event_name}
        categories={proposal.categories}
      />
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{selectedRows.length} item{selectedRows.length === 1 ? '' : 's'} selected</span>
        <span className="inline-flex items-center gap-1.5">
          <span>Selected total</span>
          <AmountText value={keptTotal} variant={selectedRows.length === 0 ? 'balance' : 'amount'} />
        </span>
      </div>
      <div className="rounded-lg border border-border">
        {proposal.matches.map((row) => (
          <AskMatchRow key={row.id} row={row} kept={keptIds.has(row.id)} onToggle={onToggle} />
        ))}
      </div>
      <LikelyRelatedTray rows={proposal.likely_related} keptIds={keptIds} onToggle={onToggle} />
    </div>
  );
}

export default AskProposalBody;
